import React from 'react';
import { useAuth } from '../../context/AuthContext';

/**
 * Parseia campaign_locked_until em diferentes formatos que podem vir da API:
 *   - "2026-04-15"                    (string YYYY-MM-DD do pspService)
 *   - "2026-04-15T00:00:00.000Z"      (ISO datetime — mysql2 converte DATE)
 *   - Date object                     (mysql2 com typeCast DATE → Date)
 *   - null/undefined/''               → retorna null (banner some)
 *
 * Setta hora pra 23:59:59 local pra representar "fim do dia".
 * Retorna Date válido ou null (nunca Invalid Date).
 */
function parseLockDate(value) {
  if (!value) return null;
  let d;
  if (value instanceof Date) {
    d = new Date(value);
  } else if (typeof value === 'string') {
    // Pega só a parte YYYY-MM-DD (primeiros 10 chars) pra normalizar — ignora
    // qualquer "T00:00:00" ou timezone que venha concatenado. Evita bug onde
    // fazer `value + 'T23:59:59'` gerava lixo do tipo "ISO completa + T23:59:59".
    const dateOnly = value.slice(0, 10);
    d = new Date(dateOnly + 'T23:59:59');
  } else {
    return null;
  }
  return isNaN(d.getTime()) ? null : d;
}

/**
 * DiscountBanner — contador regressivo do desconto de campanha do tenant.
 *
 * Regras:
 * - Só aparece se o tenant tem `campaign_locked_until` setado E faltam ≤30 dias
 *   pra expirar
 * - Fica hidden quando campaign_locked_until <= hoje OU parse falha
 * - Não tem dismiss persistente: o objetivo é avisar, não entupir
 */
export default function DiscountBanner() {
  const { user } = useAuth();
  const tenant = user?.tenant;
  if (!tenant?.campaign_locked_until) return null;

  const lockedUntil = parseLockDate(tenant.campaign_locked_until);
  // Parse falhou → esconde em vez de mostrar "Invalid Date / NaN dias"
  if (!lockedUntil) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const msLeft = lockedUntil.getTime() - today.getTime();
  const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24));

  // Sanity check final — se virar NaN por algum motivo inesperado, esconde
  if (!Number.isFinite(daysLeft)) return null;

  // Desconto já expirou → não mostra (sistema já está cobrando preço cheio)
  if (daysLeft < 0) return null;
  // Ainda falta mais de 30 dias → não polui a UI
  if (daysLeft > 30) return null;

  const isCritical = daysLeft <= 7;
  const isWarning  = daysLeft <= 15 && !isCritical;

  const palette = isCritical
    ? { bg: '#fef2f2', border: '#fca5a5', text: '#991b1b', icon: '⏰' }
    : isWarning
      ? { bg: '#fff7ed', border: '#fcd34d', text: '#92400e', icon: '⌛' }
      : { bg: '#fefce8', border: '#fde68a', text: '#854d0e', icon: '💡' };

  const formatDate = lockedUntil.toLocaleDateString('pt-BR', {
    day: '2-digit', month: 'long', year: 'numeric',
  });

  const message = daysLeft === 0
    ? `Seu desconto promocional expira hoje. A partir da próxima fatura, o valor volta ao preço cheio do plano.`
    : daysLeft === 1
      ? `Seu desconto promocional expira amanhã (${formatDate}). A próxima fatura já virá no preço cheio do plano.`
      : `Seu desconto promocional expira em ${daysLeft} dias (${formatDate}). Depois disso, a cobrança volta ao preço cheio do plano.`;

  const severity = isCritical ? 'critical' : isWarning ? 'warning' : 'info';
  return (
    <div
      className={`discount-banner discount-banner--${severity}`}
      role="status"
      aria-live="polite"
      style={{
        margin: '8px 16px 0',
        padding: '10px 14px',
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        color: palette.text,
        borderRadius: 10,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        fontSize: 13,
      }}
    >
      <span style={{ fontSize: 20, flexShrink: 0 }}>{palette.icon}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, marginBottom: 2 }}>
          Desconto da campanha • {daysLeft > 0 ? `${daysLeft} ${daysLeft === 1 ? 'dia' : 'dias'} restante${daysLeft === 1 ? '' : 's'}` : 'expira hoje'}
        </div>
        <div style={{ lineHeight: 1.4 }}>{message}</div>
      </div>
    </div>
  );
}
